<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { Edit16, TrashCan16 } from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'dashboard/components-next/polaris/ui/card';
import CardPopover from '../components/CardPopover.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  description: { type: String, default: '' },
  assignedAgentCount: { type: Number, default: 0 },
  users: { type: Array, default: () => [] },
  isFetchingUsers: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete', 'fetchUsers']);

const { t } = useI18n();

const users = computed(() => {
  return props.users.map(user => {
    return {
      name: user.name,
      key: user.id,
      email: user.email,
      avatarUrl: user.avatarUrl,
    };
  });
});

const handleEdit = () => {
  emit('edit', props.id);
};

const handleDelete = () => {
  emit('delete', props.id);
};

const handleFetchUsers = () => {
  if (props.users?.length > 0) return;
  emit('fetchUsers', props.id);
};
</script>

<!-- Rebuilt inside on the kit's PolicyCard (phase 9.8): name and description, the count that opens the list,
     Edit and Delete (Delete still asks through the page's dialog), and the facts under them. Same props and events. -->
<template>
  <Card class="gap-3 py-4" data-policy-card>
    <div class="flex flex-wrap items-start gap-x-3 gap-y-2 px-4">
      <CardHeader class="min-w-48 flex-1 p-0">
        <CardTitle class="line-clamp-2">{{ name }}</CardTitle>
        <CardDescription v-if="description" class="line-clamp-2">
          {{ description }}
        </CardDescription>
      </CardHeader>
      <div class="flex flex-wrap items-center gap-1">
        <CardPopover
          :title="
            t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.CARD.POPOVER')
          "
          icon="i-lucide-users-round"
          :count="assignedAgentCount"
          :items="users"
          :is-fetching="isFetchingUsers"
          @fetch="handleFetchUsers"
        />
        <Button type="button" variant="outline" size="sm" @click="handleEdit">
          <Edit16 aria-hidden="true" />
          {{ t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.CARD.EDIT') }}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          :aria-label="t('SETTINGS_TABLE.DELETE')"
          @click="handleDelete"
        >
          <TrashCan16 aria-hidden="true" />
        </Button>
      </div>
    </div>
  </Card>
</template>
